import React, { useEffect, useState } from 'react';
import { api } from '../../../../Api-Endpoints/apiEndpoint';
import { FaFolder, FaPlus, FaMinus } from 'react-icons/fa';
import { RiFlowChart } from 'react-icons/ri';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import {
  LoadProceduresRequest,
  LoadProceduresRequestEnd,
} from '../../../../Redux/ProcedureReducer';
import Loader from '../../../../Components/Butten/Loader';

// Recursive Folder Component
const Folder = ({
  folder,
  selectedProcedures,
  toggleProcedure,
  handleSelectAll,
}) => {
  const { _id, name, procedures, children } = folder;
  const [isOpen, setIsOpen] = useState(false);

  const handleFolderSelect = e => {
    handleSelectAll(folder, e.target.checked);
  };

  const isSelected = () => {
    // If no procedures or children, return false since there's nothing to select
    if (procedures.length === 0 && children.length === 0) return false;

    // Check if all procedures in current folder are selected
    const allProceduresSelected =
      procedures.length === 0 ||
      procedures.some(proc => selectedProcedures.has(proc._id));

    // Check if all children have at least one selected item
    const allChildrenSelected =
      children.length === 0 || children.some(child => isChildSelected(child));

    return allProceduresSelected && allChildrenSelected;
  };

  const isChildSelected = childFolder => {
    // Check if any procedure in this child folder is selected
    const hasSelectedProcedures = childFolder.procedures.some(proc =>
      selectedProcedures.has(proc._id)
    );

    // Check if any grandchild has selected procedures
    const hasSelectedGrandchildren = childFolder.children.some(grandchild =>
      isChildSelected(grandchild)
    );

    return hasSelectedProcedures || hasSelectedGrandchildren;
  };

  return (
    <div style={{ paddingLeft: '20px', borderLeft: '1px solid #ddd' }}>
      <div className="flex items-center gap-1">
        <span onClick={() => setIsOpen(!isOpen)} style={{ cursor: 'pointer' }}>
          {isOpen ? <FaMinus /> : <FaPlus />}
        </span>
        <label className="text-red-500 flex items-center gap-1">
          <FaFolder />
          <input
            className="text-red-500"
            type="checkbox"
            checked={isSelected()}
            onChange={handleFolderSelect}
          />
          <span>{name} (Select all in folder)</span>
        </label>
      </div>

      {isOpen && (
        <>
          {procedures.map(proc => (
            <div key={proc._id}>
              <label className="flex items-center gap-1">
                <RiFlowChart />
                <input
                  type="checkbox"
                  checked={selectedProcedures.has(proc._id)}
                  onChange={() => toggleProcedure(proc._id)}
                />
                <span>{proc.name}</span>
              </label>
            </div>
          ))}
          {children.map(childFolder => (
            <Folder
              key={childFolder._id}
              folder={childFolder}
              selectedProcedures={selectedProcedures}
              toggleProcedure={toggleProcedure}
              handleSelectAll={handleSelectAll}
            />
          ))}
        </>
      )}
    </div>
  );
};

// Main ProcedureSelector Component
const ProcedureSelector = ({ prevData, setProcedureIds, isEditMode }) => {
  const [selectedProcedures, setSelectedProcedures] = useState(new Set());
  const [folderData, setFolderData] = useState([]);
  const dispatch = useDispatch();
  const { isLoading } = useSelector(
    state => ({
      isLoading: state.Procedure.isLoading,
    }),
    shallowEqual
  );
  useEffect(() => {
    dispatch(LoadProceduresRequest());
    api.get('/folder/getfolders/6719520b320d80deacb621e7').then(res => {
      if (res.status === 200) {
        setFolderData(res?.data);
      }
      dispatch(LoadProceduresRequestEnd());
    });

    if (isEditMode) {
      setSelectedProcedures(prevSelected => {
        const newSelected = new Set(prevSelected);

        // Add each procedure ID individually to the new Set
        prevData.procedures.forEach(procedure => {
          newSelected.add(procedure?.id || procedure?.procedure?._id);
        });

        return newSelected;
      });
    }
  }, []);

  useEffect(() => {
    setProcedureIds(Array.from(selectedProcedures));
  }, [selectedProcedures]);

  const toggleProcedure = procedureId => {
    setSelectedProcedures(prevSelected => {
      const newSelected = new Set(prevSelected);
      if (newSelected.has(procedureId)) {
        newSelected.delete(procedureId);
      } else {
        newSelected.add(procedureId);
      }
      return newSelected;
    });
  };

  const handleSelectAll = (folder, isSelected) => {
    const updateSelection = (folder, newSelected) => {
      folder.procedures.forEach(proc => {
        if (isSelected) {
          newSelected.add(proc._id);
        } else {
          newSelected.delete(proc._id);
        }
      });

      folder.children.forEach(child => {
        updateSelection(child, newSelected); // Recursively update child folders
      });
    };

    setSelectedProcedures(prevSelected => {
      const newSelected = new Set(prevSelected); // Create a new Set
      updateSelection(folder, newSelected); // Update selections
      return newSelected; // Return the updated Set
    });
  };

  return (
    <div>
      <h2>Select Procedures/Folders</h2>
      {isLoading ? (
        <Loader></Loader>
      ) : (
        folderData?.folders?.map(folder => (
          <Folder
            key={folder._id}
            folder={folder}
            selectedProcedures={selectedProcedures}
            toggleProcedure={toggleProcedure}
            handleSelectAll={handleSelectAll}
          />
        ))
      )}
    </div>
  );
};

export default ProcedureSelector;
